import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Trophy } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AdminLayout";
import { ClientStatusBadge } from "@/components/Badges";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const SORTS = [
  { k: "paid_friends", label: "Paid orders" },
  { k: "signed_up", label: "Signed up" },
  { k: "friends_spent", label: "Total spent" },
];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-border bg-card p-5">
    <p className="eyebrow">{label}</p>
    <p className="mt-1 font-mono-data text-2xl" data-testid={testId}>{value}</p>
  </div>
);

function FriendRows({ r }) {
  return r.friends.map((f) => (
    <TableRow key={f.id} className="bg-muted/20" data-testid={`referral-friend-${f.id}`}>
      <TableCell />
      <TableCell className="pl-8 text-sm"><Link to={`/admin/clients/${f.id}`} className="hover:text-emerald-300">{f.name}</Link> <span className="text-xs text-muted-foreground">joined {fmtDate(f.joined)}</span></TableCell>
      <TableCell><ClientStatusBadge status={f.status} /></TableCell>
      <TableCell className="font-mono-data text-sm">{f.paid_orders} paid</TableCell>
      <TableCell className="font-mono-data text-sm">{money(f.spent)}</TableCell>
      <TableCell colSpan={2} />
    </TableRow>
  ));
}

export default function Referrals() {
  const [d, setD] = useState(null);
  const [sort, setSort] = useState("paid_friends");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/referrals").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const rows = useMemo(() => [...(d?.referrers || [])].sort((a, b) => b[sort] - a[sort] || b.paid_friends - a.paid_friends || b.friends_spent - a.friends_spent), [d, sort]);
  if (!d) return null;

  return (
    <div>
      <PageHeader eyebrow="Growth" title="Referrals" />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Clients referring" value={d.totals.referrers} testId="referrals-total-referrers" />
        <Stat label="Friends signed up" value={d.totals.signed_up} testId="referrals-total-signed-up" />
        <Stat label="Friends who paid" value={d.totals.paid_friends} testId="referrals-total-paid" />
        <Stat label="Friends' paid sales" value={money(d.totals.friends_spent)} testId="referrals-total-spent" />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Rank by</span>
        {SORTS.map((s) => (
          <button key={s.k} onClick={() => setSort(s.k)} data-testid={`referrals-sort-${s.k}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${sort === s.k ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead><TableHead>Client</TableHead><TableHead>Signed up</TableHead><TableHead>Paid orders</TableHead>
              <TableHead>Total spent</TableHead><TableHead>Rewards earned</TableHead><TableHead>Rewards used</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <Fragment key={r.id}>
                <TableRow className="cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`referrer-row-${r.id}`}>
                  <TableCell className="font-mono-data">{i < 3 ? <Trophy className={`h-4 w-4 ${["text-amber-400", "text-slate-300", "text-orange-400"][i]}`} /> : i + 1}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 font-medium">{open === r.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{r.name}</span>
                    <span className="pl-5 text-xs text-muted-foreground">{r.email}</span>
                  </TableCell>
                  <TableCell className="font-mono-data" data-testid={`referrer-signed-up-${r.id}`}>{r.signed_up}</TableCell>
                  <TableCell className="font-mono-data text-emerald-400" data-testid={`referrer-paid-${r.id}`}>{r.paid_friends}</TableCell>
                  <TableCell className="font-mono-data" data-testid={`referrer-spent-${r.id}`}>{money(r.friends_spent)}</TableCell>
                  <TableCell className="font-mono-data">{r.earned}</TableCell>
                  <TableCell className="font-mono-data">{r.used}</TableCell>
                </TableRow>
                {open === r.id && <FriendRows r={r} />}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="referrals-empty">No referrals yet. Clients find their link under "Refer a Friend".</p>}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Paid orders counts friends with at least one paid order (cancelled orders excluded). Click a client to see their friends.</p>
    </div>
  );
}
